import React from 'react';
import { Trash2, BookOpen, BookText, GraduationCap, Edit2 } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Word, Theme } from '../types';

interface WordListProps {
  words: Word[];
  onDelete: (word: string) => void;
  onEdit: (word: Word) => void;
  theme: Theme;
}

function WordList({ words, onDelete, onEdit, theme }: WordListProps) {
  return (
    <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
      <AnimatePresence>
        {words.map((word, index) => (
          <motion.div
            key={word.word}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95 }}
            transition={{ delay: index * 0.1 }}
            whileHover={{ scale: 1.02 }}
            className={`relative group ${
              theme === 'dark' 
                ? 'bg-gray-800 border-gray-700' 
                : 'bg-white border-purple-100'
            } rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow border`}
          >
            <div className="flex justify-between items-start mb-3">
              <h3 className={`text-xl font-semibold ${
                theme === 'dark' ? 'text-white' : 'text-purple-900'
              }`}>
                {word.word}
              </h3>
              <div className="flex items-center gap-2">
                {(!word.definition || word.definition.includes('Manual entry needed')) && (
                  <motion.button
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                    onClick={() => onEdit(word)}
                    className={`text-yellow-400 hover:text-yellow-500 transition-colors`}
                    title="Definition needed - Click to edit"
                  >
                    <Edit2 size={18} />
                  </motion.button>
                )}
                <motion.button
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                  onClick={() => onDelete(word.word)}
                  className={`text-purple-400 hover:text-purple-600 transition-colors ${
                    theme === 'dark' ? 'hover:text-purple-300' : ''
                  }`}
                  title="Delete word"
                >
                  <Trash2 size={18} />
                </motion.button>
              </div>
            </div>
            
            <div className="space-y-3">
              <div>
                <div className="flex items-center gap-2 text-sm font-medium text-purple-700 mb-1">
                  <BookOpen size={16} />
                  <span>Definition</span>
                </div>
                <p className={`${
                  theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                } ${
                  word.definition?.includes('Manual entry needed') 
                    ? 'text-yellow-500 italic'
                    : ''
                }`}>
                  {word.definition || 'Definition needed'}
                </p>
              </div>

              {word.source && (
                <div className="flex items-center gap-2 text-sm text-purple-500 mt-4">
                  <BookOpen size={16} />
                  <span>{word.source}</span>
                </div>
              )}
            </div>

            <div className="mt-2 text-xs text-purple-400">
              Added: {new Date(word.date).toLocaleDateString()}
            </div>

            {/* Edit button overlay on hover */}
            <motion.button
              initial={{ opacity: 0 }}
              whileHover={{ opacity: 1 }}
              onClick={() => onEdit(word)}
              className={`absolute inset-0 flex items-center justify-center bg-black bg-opacity-50 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity`}
            >
              <span className="flex items-center gap-2 px-4 py-2 bg-white rounded-lg text-gray-900">
                <Edit2 size={16} />
                Edit Word
              </span>
            </motion.button>
          </motion.div>
        ))}
      </AnimatePresence>
      
      {words.length === 0 && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="col-span-full text-center py-12 text-purple-500"
        >
          No words found. Start by adding your first word!
        </motion.div>
      )}
    </div>
  );
}

export default WordList;